import useFullSizeModalRouteUpdater from 'hooks/useFullSizeModalRouteUpdater'
import useModal from 'hooks/useModal'
import { useIsDisabledFeaturedOnTelegram } from 'hooks/useTelegram'
import { useCallback } from 'react'
import { ModalBody, ModalHeader } from 'UIKit/AlchemyModal'
import { ModalSizeModeEnum } from 'UIKit/AlchemyModal/Modal'
import { ModalProps } from 'UIKit/Modal'
import WarningOnTelegram from 'views/Authentication/components/Wallet/WarningOnTelegram/WarningOnTelegram'
import PaymentCommitments from '../components/PaymentCommitments'
import { DepositOptionEnums, PaymentPageEnums } from '../Menu'
import { DepositOptionContent } from './components/PaymentDepositOptions'
import { PAYMENT_MODAL_THEME, StylePaymentModal } from './components/styles'
import DepositBuyCrypto from './Deposit/DepositBuyCrypto'
import DepositCrypto from './Deposit/DepositCryptos'
import DepositByFiat from './Deposit/DepositFiat'
import useCommitmentVisibility from './hooks/useCommitmentVisibility'
import Swap from './Swap'
import Tip from './Tip'
import { ICommitmentsControl, IPaymentProps } from './types'
import Withdraw from './Withdraw'

const ModalPaymentCommitments: React.FC<
  ModalProps<{
    commitmentsControl: ICommitmentsControl
  }>
> = ({ data: { commitmentsControl }, onDismiss }) => {
  const onBack = useFullSizeModalRouteUpdater('commiment', onDismiss)

  return (
    <StylePaymentModal sizeMode={ModalSizeModeEnum.Full} colorTheme={PAYMENT_MODAL_THEME} onDismiss={onBack}>
      <PaymentCommitments commitmentsControl={{ ...commitmentsControl, toggleCommitment: onBack }} />
    </StylePaymentModal>
  )
}

const MobilePayment: React.FC<ModalProps<IPaymentProps>> = ({
  data: { initialToken, defaultPage, pageData },
  onDismiss,
}) => {
  const [onPresentDepositCrypto] = useModal(DepositCrypto)
  const [onPresentDepositFiat] = useModal(DepositByFiat)
  const [onPresentDepositBuyCrypto] = useModal(DepositBuyCrypto)
  const [onPresentPaymentCommitments] = useModal(ModalPaymentCommitments)
  const commitmentsControl = useCommitmentVisibility(PaymentPageEnums.Deposit)
  const isDisabledFeaturedOnTelegram = useIsDisabledFeaturedOnTelegram()

  const handleSelectDepositPage = useCallback(
    (selectedTab: DepositOptionEnums) => {
      if (selectedTab === DepositOptionEnums.Crypto) {
        onPresentDepositCrypto({ initialToken })
      } else if (selectedTab === DepositOptionEnums.Fiat) {
        onPresentDepositFiat({ initialToken })
      } else if (selectedTab === DepositOptionEnums.BuyCrypto) {
        onPresentDepositBuyCrypto({
          onNavigateToDeposit: () => {
            onPresentDepositCrypto({ initialToken })
          },
        })
      }
    },
    [initialToken, onPresentDepositCrypto, onPresentDepositFiat],
  )

  if (defaultPage === PaymentPageEnums.Swap) return <Swap onDismiss={onDismiss} />

  if (isDisabledFeaturedOnTelegram) {
    return (
      <StylePaymentModal colorTheme={PAYMENT_MODAL_THEME} id="payment" onDismiss={onDismiss}>
        <ModalHeader
          title={
            defaultPage === PaymentPageEnums.Tip
              ? 'Tip'
              : defaultPage === PaymentPageEnums.Withdraw
              ? 'Withdraw'
              : 'Deposit'
          }
          titleAlign="center"
          colorTheme={PAYMENT_MODAL_THEME}
        />
        <ModalBody>
          <WarningOnTelegram />
        </ModalBody>
      </StylePaymentModal>
    )
  }

  if (defaultPage === PaymentPageEnums.Tip)
    return <Tip onDismiss={onDismiss} userName={pageData?.tipUserName || ''} initialToken={initialToken} />

  if (defaultPage === PaymentPageEnums.Withdraw) {
    return (
      <Withdraw
        onDismiss={onDismiss}
        data={{
          initialToken,
        }}
      />
    )
  }

  return (
    <StylePaymentModal colorTheme={PAYMENT_MODAL_THEME} id="payment" onDismiss={onDismiss}>
      <ModalHeader title="Source" titleAlign="center" colorTheme={PAYMENT_MODAL_THEME} />
      <ModalBody>
        <DepositOptionContent
          onSelect={handleSelectDepositPage}
          commitmentsControl={{
            ...commitmentsControl,
            toggleCommitment: () => {
              onPresentPaymentCommitments({
                commitmentsControl: {
                  ...commitmentsControl,
                  isShowCommitment: true,
                },
              })
            },
          }}
        />
      </ModalBody>
    </StylePaymentModal>
  )
}

export default MobilePayment
